import { Pressable, View } from "react-native";
import { useTranslation } from "react-i18next";

import { Icon } from "@/components/ui/icon";
import { Text } from "@/components/ui/text";
import { formatDisplayDate } from "@/lib/date";
import { TEST_IDS } from "@/testIdDefinition";

import type { LeaveRequestDateWindow } from "./groupLeaveRequests";

type PeriodNavigatorProps = {
    dateWindow: LeaveRequestDateWindow;
    windowShift: number;
    onShift: (nextShift: number) => void;
};

export function PeriodNavigator({
    dateWindow,
    windowShift,
    onShift,
}: PeriodNavigatorProps) {
    const { t } = useTranslation();
    const rangeLabel = t("requests.periodRange", {
        from: formatDisplayDate(dateWindow.startIso),
        to: formatDisplayDate(dateWindow.endIso),
    });
    const heading =
        windowShift === 0
            ? t("requests.periodCurrentRange", { range: rangeLabel })
            : rangeLabel;

    return (
        <View className="gap-2 px-1">
            <View className="flex-row items-center">
                <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={t("requests.periodPreviousA11y")}
                    onPress={() => onShift(windowShift - 1)}
                    testID={TEST_IDS[TEST_IDS.REQUESTS_PERIOD_PREV]}
                    className="h-10 w-10 items-center justify-center rounded-lg active:bg-accent"
                >
                    <Icon
                        name="chevron-left"
                        iconStyle="solid"
                        className="text-foreground"
                        size={16}
                    />
                </Pressable>
                <Text
                    variant="label"
                    className="flex-1 text-center"
                    testID={TEST_IDS[TEST_IDS.REQUESTS_PERIOD_LABEL]}
                >
                    {heading}
                </Text>
                <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={t("requests.periodNextA11y")}
                    onPress={() => onShift(windowShift + 1)}
                    testID={TEST_IDS[TEST_IDS.REQUESTS_PERIOD_NEXT]}
                    className="h-10 w-10 items-center justify-center rounded-lg active:bg-accent"
                >
                    <Icon
                        name="chevron-right"
                        iconStyle="solid"
                        className="text-foreground"
                        size={16}
                    />
                </Pressable>
            </View>
            {windowShift !== 0 ? (
                <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={t("requests.periodTodayA11y")}
                    onPress={() => onShift(0)}
                    testID={TEST_IDS[TEST_IDS.REQUESTS_PERIOD_TODAY]}
                    className="self-center rounded-full border border-border px-3 py-1.5 active:bg-accent"
                >
                    <Text className="font-sans-medium text-xs tracking-wide">
                        {t("requests.periodToday")}
                    </Text>
                </Pressable>
            ) : null}
        </View>
    );
}
